import throttle from 'lodash/throttle';
import {AttrsSchema, Component, jsx} from 'panel';
import type {VNode} from 'snabbdom';
import ResizeObserver from 'resize-observer-polyfill';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import * as colors from 'common/util/colors';
import {formatMetricNumber} from 'common/util';
import {TooltipEl} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {TooltipItemType, TooltipSection} from 'common/components/chart-tooltip/types';
import {ChartTooltipAttrs, ChartTooltipEl} from 'common/components/chart-tooltip';
import {getDownloadMode} from 'common/report/util';
import {DownloadMode} from 'common/report/constants';
import {DummyUrlManager} from 'common/contexts/url-manager/dummy';
import {IronContextRegistry} from 'common/report/context-registry';
import {CalendarDateRange} from 'common/widgets/date-range-picker/models/types';

import css from './index.inline.styl';
import {
  ConversionStep,
  ConversionStepBarType,
  ConversionStepLayout,
  getConversionStepLayouts,
  getNumBarsPerStep,
  getPercentLabel,
  NormalConversionStep,
  validateConversionSteps,
} from './util';

export type StepBarContextMenuEvent = CustomEvent<{
  conversionStep: NormalConversionStep;
  barIndex: number;
  originalMouseEvent: MouseEvent;
}>;

export type StepBarClickEvent = CustomEvent<{
  // in practice, clicking a bar is usually related to bar selection, so including the step index
  // is more helpful for configuring bar selection than including the basic ConversionStep itself
  stepIndex: number;
  color: string;
  isComparison: boolean;
  comparisonDateRange: CalendarDateRange | null;
  barIndex: number;
  isDropoff: boolean;
  originalMouseEvent: MouseEvent;
}>;

export enum StepHorizontalWheelDirection {
  Left = `left`,
  Right = `right`,
}

export type StepHorizontalWheelEvent = CustomEvent<{
  firstVisibleStepIndex: number;
  direction: StepHorizontalWheelDirection;
}>;

export interface BarSelection {
  stepIndex: number;
  barIndex: number;
  isDropoff: boolean;
}

interface ConversionColumnVizState {
  horizontalScrollbarHeight: number;
  hoveredStepBar: null | {
    stepIndex: number;
    barIndex: number;
    hoveredOnDropoff: boolean;
    barElement: HTMLElement;
  };
  barSelectionLookup: Map<number, Set<number>>; // key is the step index, value is a collection of selected bar indices
  dropoffBarSelectionLookup: Map<number, Set<number>>; // key is the step index, value is a collection of selected bar indices
}

type ConversionStepBarTooltipDataCallback = (
  conversionStep: NormalConversionStep,
  previousConversionStep: null | NormalConversionStep,
  barIndex: number,
  hoveredOnDropoff: boolean,
) => ChartTooltipAttrs;

interface ConversionColumnVizJsxProps {
  attrs?: {
    'enable-popover-menu'?: boolean;
    'enable-bar-click'?: boolean;
    'popover-menu-open'?: boolean;
    'min-step-width'?: number;
    'max-step-width'?: number;
  };
  props?: {
    conversionSteps: ConversionStep[];
    selectedBars?: BarSelection[];
    stepBarTooltipCallback?: ConversionStepBarTooltipDataCallback;
  };
  on?: {
    stepBarContextMenu?: (ev: StepBarContextMenuEvent) => void;
    stepBarClick?: (ev: StepBarClickEvent) => void;
    stepHorizontalWheel?: (ev: StepHorizontalWheelEvent) => void;
  };
}

const Y_AXIS_TICK_PERCENTS = [75, 50, 25, 0];
const STEP_GAP_PX = 24;
const STEP_RIGHT_PADDING_PX = 2;
const BAR_GAP_PX = 6;
const MINIMUM_BAR_WIDTH_TO_SHOW_COUNT_LABELS = 60;
const MINIMUM_BAR_WIDTH = 20;
// We set minimum step width equal to a step with 2 of the narrowest bars (MINIMUM_BAR_WIDTH). A step has multiple
// bars when the query is segmented.
const DEFAULT_MINIMUM_STEP_WIDTH = MINIMUM_BAR_WIDTH * 2 + BAR_GAP_PX + STEP_GAP_PX + STEP_RIGHT_PADDING_PX;
const MINIMUM_CSS_BAR_HEIGHT_PERCENT = 3;
export const Y_AXIS_LABEL_WIDTH_PX = 40;

export class ConversionColumnViz extends Component<
  ConversionColumnVizState,
  ConversionColumnVizJsxProps['attrs'],
  EmptyObj,
  EmptyObj,
  IronContextRegistry
> {
  static get attrsSchema(): AttrsSchema<ConversionColumnVizJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      'enable-popover-menu': `boolean`,
      'enable-bar-click': `boolean`,
      'popover-menu-open': `boolean`,
      'min-step-width': {type: `number`, default: DEFAULT_MINIMUM_STEP_WIDTH},
      'max-step-width': `number`,
    };
  }

  public get config() {
    return {
      contexts: [`urlManager` as const],
      defaultContexts: {
        urlManager: new DummyUrlManager(),
      },
      css,
      useShadowDom: true,
      template: () => this.renderTemplate(),
      defaultState: {
        horizontalScrollbarHeight: 0,
        hoveredStepBar: null,
        barSelectionLookup: new Map<number, Set<number>>(),
        dropoffBarSelectionLookup: new Map<number, Set<number>>(),
      },
    };
  }

  private _conversionSteps: ConversionStep[] = [];
  public get conversionSteps(): ConversionStep[] {
    return this._conversionSteps;
  }
  public set conversionSteps(value: ConversionStep[]) {
    validateConversionSteps(value); // will throw errors if conversion steps are invalid
    this._conversionSteps = value;
    this.stepLayouts = getConversionStepLayouts(this.conversionSteps); // cannot be in state because it must always be sync with `this.conversionSteps`
  }

  private _selectedBars: BarSelection[] = [];
  public get selectedBars(): BarSelection[] {
    return this._selectedBars;
  }
  public set selectedBars(value: BarSelection[]) {
    this._selectedBars = value;

    const barSelectionLookup = new Map<number, Set<number>>();
    for (const selection of this.selectedBars) {
      if (!barSelectionLookup.has(selection.stepIndex)) {
        barSelectionLookup.set(selection.stepIndex, new Set<number>());
      }
      if (!selection.isDropoff) {
        barSelectionLookup.get(selection.stepIndex).add(selection.barIndex);
      }
    }

    const dropoffBarSelectionLookup = new Map<number, Set<number>>();
    for (const selection of this.selectedBars) {
      if (!dropoffBarSelectionLookup.has(selection.stepIndex)) {
        dropoffBarSelectionLookup.set(selection.stepIndex, new Set<number>());
      }
      if (selection.isDropoff) {
        dropoffBarSelectionLookup.get(selection.stepIndex).add(selection.barIndex);
      }
    }

    this.update({barSelectionLookup, dropoffBarSelectionLookup});
  }

  public stepBarTooltipCallback?: ConversionStepBarTooltipDataCallback;

  private stepLayouts: ConversionStepLayout[] = []; // each layout corresponds to the ConversionStep of the same array index in `this.conversionSteps`
  private multiResizeObserver?: ResizeObserver;
  private scrollContainer?: HTMLElement;
  private lastScrollLeft?: number;

  public connectedCallback() {
    this.multiResizeObserver = new ResizeObserver((_) => {
      this.lastScrollLeft = this.scrollContainer.scrollLeft;
      this.update({horizontalScrollbarHeight: this.scrollContainer.offsetHeight - this.scrollContainer.clientHeight});
    });

    super.connectedCallback();
  }

  public disconnectedCallback() {
    this.multiResizeObserver.disconnect();

    super.disconnectedCallback();
  }

  private renderTemplate() {
    return (
      <div
        sel='.root'
        class={{'bar-label-gap': this.showBarLabels()}}
        style={{'--horizontal-scrollbar-height': `${this.state.horizontalScrollbarHeight}px`}}
      >
        <div sel='.y-axis'>
          <div sel='.y-axis-labels' style={{width: `${Y_AXIS_LABEL_WIDTH_PX}px`}}>
            {Y_AXIS_TICK_PERCENTS.map((percent) => (
              <div sel='.label' style={{bottom: `${percent}%`}}>
                {percent}%
              </div>
            ))}
          </div>
        </div>

        {this.renderScrollableContent()}

        {this.renderBarTooltip()}
      </div>
    );
  }

  private renderScrollableContent() {
    const isDownloadMode = getDownloadMode(this.getContext(`urlManager`)) !== DownloadMode.None;
    return (
      <div
        sel='.scroll-container'
        class={{'disable-horizontal-scroll': isDownloadMode}}
        on={{
          wheel: this.handleScrollContainerWheel.bind(this),
        }}
        hook={{
          insert: (vnode: VNode): void => {
            this.multiResizeObserver.observe(vnode.elm as Element);
            this.scrollContainer = vnode.elm as HTMLElement;
            this.lastScrollLeft = this.scrollContainer.scrollLeft;
            this.update(); // note: scrollContainer is used to calculate step width
          },
          remove: (vnode: VNode, removeCallback: () => void): void => {
            this.multiResizeObserver.unobserve(vnode.elm as Element);
            delete this.scrollContainer;
            removeCallback();
          },
        }}
      >
        <div
          sel='.steps'
          style={{'grid-template-columns': `repeat(${this.conversionSteps.length}, ${this.getStepWidth()}px)`}}
          hook={{
            insert: (vnode: VNode): void => this.multiResizeObserver.observe(vnode.elm as Element),
            remove: (vnode: VNode, removeCallback: () => void): void => {
              this.multiResizeObserver.unobserve(vnode.elm as Element);
              removeCallback();
            },
          }}
        >
          <div sel='.gridlines' style={{'grid-column': `1 / span ${this.conversionSteps.length}`}}>
            {Y_AXIS_TICK_PERCENTS.map((percent) => (
              <div sel='.y-axis-gridline' style={{bottom: `${percent}%`}} />
            ))}
          </div>

          {this.conversionSteps.map((step, stepIndex) => {
            if (step.placeholder === true) {
              return (
                <div
                  sel='.step-label'
                  style={{
                    'grid-column': `${stepIndex + 1}`,
                    'padding-left': `${this.getStepGap() / 2}px`,
                  }}
                >
                  -
                </div>
              );
            }

            const stepLayout = this.stepLayouts[stepIndex];
            return (
              <>
                {/**
                 * Each bar group is an independent grid where each bar's width is "1fr".
                 * This allows us to configure each conversion step's group of bars as a whole where each bar's width is managed by CSS grid,
                 * this should be more precise and responsive than configuring each bar's width and then adding it up to a required group width.
                 * In some cases, we require a specific width for each whole bar group to align with a sibling widget (eg. a table's columns).
                 **/}
                <div
                  sel='.bar-group'
                  attrs={{elref: `barGroup`}}
                  style={{
                    'column-gap': `${BAR_GAP_PX}px`,
                    'grid-column': `${stepIndex + 1}`,
                    'padding-left': `${this.getStepGap() / 2}px`,
                    'padding-right': `${this.getStepGap() / 2 + STEP_RIGHT_PADDING_PX}px`,
                  }}
                >
                  {step.bars.map((bar, barIndex) => {
                    const barLayout = stepLayout.barLayouts[barIndex];
                    const barType = bar.type ?? ConversionStepBarType.Normal;
                    const barColor = bar.color ?? colors.getChartSegmentColorCombo(barIndex).primary;

                    // The rendered CSS bar heights must be clamped to a minimum height for UX reasons.
                    // Heights are clamped during render instead of during layout generation so that layout generation logic can be simpler for testing.
                    const cssHeightPercent = Math.max(MINIMUM_CSS_BAR_HEIGHT_PERCENT, barLayout.heightPercent * 100);
                    const cssDropoffHeightPercent = Math.min(
                      100 - cssHeightPercent,
                      Math.max(MINIMUM_CSS_BAR_HEIGHT_PERCENT, barLayout.dropoffHeightPercent * 100),
                    );

                    // TODO: track scroll events
                    return (
                      <div
                        sel='.bar-column'
                        attrs={{elref: `barColumn`}}
                        class={{'popover-menu-enabled': this.attr(`enable-popover-menu`)}}
                        style={{
                          '--bar-color': barColor,
                          'grid-column': `${barIndex + 1}`,
                        }}
                        on={{
                          click: this.attr(`enable-popover-menu`)
                            ? (ev: MouseEvent) => this.handleConversionStepContextMenu(ev, step, barIndex)
                            : null,
                        }}
                      >
                        {bar.conversionRate < 1 ? (
                          <div
                            sel='.dropoff-bar.rounded-top'
                            class={{
                              selected: this.state.dropoffBarSelectionLookup.get(stepIndex)?.has(barIndex) ?? false,
                              'click-enabled': this.attr(`enable-bar-click`),
                              'comparison-type': barType === ConversionStepBarType.Comparison,
                            }}
                            style={{
                              '--selected-gradient': `linear-gradient(to bottom, ${barColor} 0%, ${colors.rgbaStringFromHexString(
                                barColor,
                                0.05,
                              )} 100%)`,
                              // faded gradient is equivalent to 30% opacity of the selected gradient
                              // opacity is applied manually instead of using the "opacity" CSS property because we don't want to apply opacity to the :before pseudoelement
                              '--faded-gradient': `linear-gradient(to bottom, ${colors.rgbaStringFromHexString(
                                barColor,
                                0.3,
                              )} 0%, ${colors.rgbaStringFromHexString(barColor, 0.015)} 100%)`,
                              bottom: `${cssHeightPercent}%`,
                              height: `${cssDropoffHeightPercent}%`,
                            }}
                            on={{
                              mousemove: (ev: MouseEvent) => {
                                const hoveredStepBar = {
                                  stepIndex,
                                  barIndex,
                                  barElement: ev.currentTarget as HTMLElement,
                                  hoveredOnDropoff: true,
                                };
                                this.update({hoveredStepBar});
                              },
                              mouseleave: () => this.update({hoveredStepBar: null}),
                              click: this.attr(`enable-bar-click`)
                                ? (ev: MouseEvent) =>
                                    this.handleConversionStepBarClick(ev, {
                                      isComparison: barType === ConversionStepBarType.Comparison,
                                      stepIndex,
                                      barIndex,
                                      isDropoff: true,
                                      color: barColor,
                                    })
                                : null,
                            }}
                          />
                        ) : null}
                        <div
                          sel='.conversion-bar'
                          class={{
                            selected: this.state.barSelectionLookup.get(stepIndex)?.has(barIndex) ?? false,
                            'click-enabled': this.attr(`enable-bar-click`),
                            'rounded-top': bar.conversionRate >= 1,
                            'comparison-type': barType === ConversionStepBarType.Comparison,
                          }}
                          style={{
                            height: `${cssHeightPercent}%`,
                          }}
                          on={{
                            mousemove: (ev: MouseEvent) => {
                              const hoveredStepBar = {
                                stepIndex,
                                barIndex,
                                barElement: ev.currentTarget as HTMLElement,
                                hoveredOnDropoff: false,
                              };
                              this.update({hoveredStepBar});
                            },
                            mouseleave: () => this.update({hoveredStepBar: null}),
                            click: this.attr(`enable-bar-click`)
                              ? (ev: MouseEvent) =>
                                  this.handleConversionStepBarClick(ev, {
                                    isComparison: barType === ConversionStepBarType.Comparison,
                                    stepIndex,
                                    barIndex,
                                    isDropoff: false,
                                    color: barColor,
                                  })
                              : null,
                          }}
                        />

                        {this.showBarLabels() ? (
                          <div sel='.bar-count-label' style={{bottom: `${cssHeightPercent}%`}}>
                            <div>{getPercentLabel(bar.conversionRate)}</div>
                            <div sel='.value'>{formatMetricNumber(bar.value)}</div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>

                <div
                  sel='.step-label'
                  style={{
                    'grid-column': `${stepIndex + 1}`,
                    'padding-left': `${this.getStepGap() / 2}px`,
                  }}
                  attrs={{title: step.tooltipName || step.displayName}}
                >
                  <span sel='.step-number'>{stepIndex + 1}</span>
                  {step.displayName}

                  <TooltipEl attrs={{placement: Placement.Top, 'max-width': `300px`, offset: 0}}>
                    {step.tooltipName || step.displayName}
                  </TooltipEl>
                </div>
              </>
            );
          })}
        </div>
      </div>
    );
  }

  private renderBarTooltip() {
    const isPopoverMenuOpen = this.attr(`popover-menu-open`);
    const {hoveredStepBar} = this.state;
    if (hoveredStepBar && !isPopoverMenuOpen) {
      const conversionStep = this.conversionSteps[hoveredStepBar.stepIndex] as NormalConversionStep;
      const previousConversionStep: null | NormalConversionStep = (() => {
        for (let i = Math.max(0, hoveredStepBar.stepIndex - 1); i >= 0; i--) {
          if (this.conversionSteps[i].placeholder !== true) {
            return this.conversionSteps[i] as NormalConversionStep;
          }
        }
        return null;
      })();

      const tooltipCallback: ConversionStepBarTooltipDataCallback =
        this.stepBarTooltipCallback ?? this.getDefaultTooltipData;
      const tooltipAttrs = tooltipCallback(
        conversionStep,
        previousConversionStep,
        hoveredStepBar.barIndex,
        hoveredStepBar.hoveredOnDropoff,
      );

      if (tooltipAttrs.sections.length > 0) {
        return (
          <ChartTooltipEl
            sel='.bar-tooltip'
            attrs={tooltipAttrs}
            props={{positioning: {positionParent: hoveredStepBar.barElement}}}
          />
        );
      }
    }
    return null;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private getDefaultTooltipData(
    conversionStep: NormalConversionStep,
    previousConversionStep: null | NormalConversionStep,
    barIndex: number,
    hoveredOnDropoff: boolean,
  ): ChartTooltipAttrs {
    const bar = conversionStep.bars[barIndex];
    const title: string = (() => {
      if (bar.displayName) {
        return bar.displayName;
      }
      if (conversionStep.bars.length === 1) {
        return conversionStep.tooltipName ?? conversionStep.displayName;
      }
      return ``;
    })();

    const percent = hoveredOnDropoff ? 1 - bar.conversionRate : bar.conversionRate;
    const percentDescription = hoveredOnDropoff ? `drop-off` : `converted`;

    const dropoffValue = previousConversionStep
      ? previousConversionStep.bars[barIndex].value - conversionStep.bars[barIndex].value
      : 0;
    const count = hoveredOnDropoff ? dropoffValue : conversionStep.bars[barIndex].value;

    const tooltipSections: TooltipSection[] = [
      {
        rows: [{items: [{text: title, type: TooltipItemType.Strong}]}],
      },
      {
        rows: [
          {
            items: [
              {text: getPercentLabel(percent), type: TooltipItemType.Data},
              {text: percentDescription, type: TooltipItemType.Text},
            ],
          },
          {
            items: [
              {text: count.toString(), type: TooltipItemType.Data},
              {text: `total times`, type: TooltipItemType.Text},
            ],
          },
        ],
      },
    ];

    const barColor = conversionStep.bars[barIndex].color ?? colors.getChartSegmentColorCombo(barIndex).primary;

    return {sections: tooltipSections, 'title-color': barColor};
  }

  private getStepGap(): number {
    // px
    // When each step only has one bar, we can reduce the gap between steps to reduce wasted whitespace.
    const barsPerStep = getNumBarsPerStep(this.conversionSteps);
    if (barsPerStep <= 1) {
      return BAR_GAP_PX;
    }

    return STEP_GAP_PX;
  }

  private getEstimatedBarWidth(): number {
    const stepWidth = this.getStepWidth();
    const barsPerStep = getNumBarsPerStep(this.conversionSteps);

    if (barsPerStep <= 0) {
      return 0;
    }

    // Note: this is only an estimate of the bar width for showing/hiding certain UI elements,
    //       each bar's true width is controlled by CSS grid sizing instead of JS
    return (stepWidth - this.getStepGap() - STEP_RIGHT_PADDING_PX - (barsPerStep - 1) * BAR_GAP_PX) / barsPerStep;
  }

  private showBarLabels(): boolean {
    return this.getEstimatedBarWidth() >= MINIMUM_BAR_WIDTH_TO_SHOW_COUNT_LABELS;
  }

  private handleConversionStepContextMenu(
    ev: MouseEvent,
    conversionStep: NormalConversionStep,
    barIndex: number,
  ): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.dispatchEvent(
      new CustomEvent(`stepBarContextMenu`, {
        detail: {
          conversionStep,
          barIndex,
          originalMouseEvent: ev,
        },
      }) as StepBarContextMenuEvent,
    );
  }

  private handleConversionStepBarClick(
    ev: MouseEvent,
    {
      isComparison,
      stepIndex,
      barIndex,
      isDropoff,
      color,
    }: {
      isComparison: boolean;
      color: string;
      stepIndex: number;
      barIndex: number;
      isDropoff: boolean;
    },
  ): void {
    this.dispatchEvent(
      new CustomEvent(`stepBarClick`, {
        detail: {
          stepIndex,
          barIndex,
          isComparison,
          isDropoff,
          color,
          originalMouseEvent: ev,
        },
      }) as StepBarClickEvent,
    );
  }

  private handleScrollContainerWheel(_: WheelEvent): void {
    const scrollContainer = this.getScrollContainer();
    if (scrollContainer) {
      const scrollLeft = scrollContainer.scrollLeft;
      // we need to calculate our own deltaX instead of using `ev.deltaX` because the "wheel" event behavior varies across browsers
      // and its delta values don't necessarily reflect the content's scrolling direction
      const deltaX = scrollLeft - this.lastScrollLeft;
      this.lastScrollLeft = scrollLeft;

      if (deltaX !== 0) {
        this.throttledDispatchStepHorizontalScrollEvent(deltaX);
      }
    }
  }

  private throttledDispatchStepHorizontalScrollEvent: (deltaX: number) => void = throttle((deltaX: number): void => {
    const stepWidth = this.getStepWidth();
    const firstVisibleStepIndex = Math.round(this.lastScrollLeft / stepWidth);
    const horizontalScrollEvent: StepHorizontalWheelEvent = new CustomEvent(`stepHorizontalWheel`, {
      detail: {
        firstVisibleStepIndex,
        direction: deltaX > 0 ? StepHorizontalWheelDirection.Right : StepHorizontalWheelDirection.Left,
      },
    });
    this.dispatchEvent(horizontalScrollEvent);
  }, 500);

  public getStepWidth(): number {
    const barsPerStep = getNumBarsPerStep(this.conversionSteps);
    const minimumStepWidthForBars =
      barsPerStep > 1
        ? // Math.max adds a little step width when only 2 segments are selected
          MINIMUM_BAR_WIDTH * Math.max(barsPerStep, 3) +
          (this.getStepGap() + STEP_RIGHT_PADDING_PX + (barsPerStep - 1) * BAR_GAP_PX)
        : MINIMUM_BAR_WIDTH + this.getStepGap() + STEP_RIGHT_PADDING_PX;
    const minimumStepWidth = Math.max(this.attr(`min-step-width`), minimumStepWidthForBars);

    // if possible, return a step width that fills the whole scrollContainer width (or as much as max-step-width allows)
    if (this.scrollContainer && this.conversionSteps.length > 0) {
      const visibleWidth = this.scrollContainer.offsetWidth;
      if (minimumStepWidth * this.conversionSteps.length < visibleWidth) {
        const fillingStepWidth = visibleWidth / this.conversionSteps.length;
        const maxStepWidth = this.attr(`max-step-width`);
        return maxStepWidth ? Math.min(maxStepWidth, fillingStepWidth) : fillingStepWidth;
      }
    }

    return minimumStepWidth;
  }

  public getScrollContainer(): null | HTMLElement {
    return this.scrollContainer ?? null;
  }
}

export const ConversionColumnVizEl = defineAndMakeJsxFactory<ConversionColumnVizJsxProps>(
  `mp-conversion-column-viz`,
  ConversionColumnViz,
);
export const ConversionColumnVizReact = panel2react<ConversionColumnVizJsxProps>(
  `mp-conversion-column-viz`,
  ConversionColumnViz,
);
